import { api, API_BASE_URL } from "./api";

export interface RomDto {
  id: string;

  deviceId: string;

  label: string;

  type: "local" | "remote";

  status: "active" | "deleted";

  workspaceId: string;

  createdAt: string;

  updatedAt: string;
}

export interface RomParticipantDto {
  id: string;

  romId: string;

  deviceId: string;

  label: string;

  role: string;

  platform: string | null;

  joinedAt: string;

  leftAt: string | null;

  lastSeenAt: string;

  phase: "pairing" | "streaming";
}

export interface ActiveRomForDeviceResponse {
  data: { rom: RomDto; participant: RomParticipantDto } | null;
}

export interface RomMessageDto {
  id: string;

  romId: string;

  deviceId: string;

  senderName: string;

  text: string;

  isHost: boolean;

  createdAt: string;

  seq: number;
}

async function heartbeat(romId: string, participantId: string): Promise<void> {
  await api.authPost<void>(
    `/roms/${romId}/participants/${participantId}/heartbeat`,
  );
}

async function leave(romId: string, participantId: string): Promise<void> {
  await api.authPost<void>(
    `/roms/${romId}/participants/${participantId}/leave`,
  );
}

/**
 * Fire-and-forget leave suitable for `beforeunload`.
 * Uses `navigator.sendBeacon` so it survives page unload.
 *
 * Beacon cannot set custom headers, so the accessKey is passed as a query
 * param — the backend's AccessKeyGuard accepts `?accessKey=...` as a fallback
 * specifically for this case.
 */

function leaveBeacon(romId: string, participantId: string): void {
  try {
    const accessKey = localStorage.getItem("@Guest/accessKey");

    if (!accessKey) return;

    const url = `${API_BASE_URL}/roms/${romId}/participants/${participantId}/leave?accessKey=${encodeURIComponent(accessKey)}`;

    const blob = new Blob([""], { type: "text/plain" });

    navigator.sendBeacon(url, blob);
  } catch {
    /* best-effort */
  }
}

async function updatePhase(
  romId: string,

  participantId: string,

  phase: "pairing" | "streaming",
): Promise<void> {
  await api.authPatch<void>(
    `/roms/${romId}/participants/${participantId}/phase`,

    { phase },
  );
}

async function fetchMessagesSince(
  romId: string,

  since: number,

  limit = 100,
): Promise<RomMessageDto[]> {
  const res = await api.authGet<{ data: RomMessageDto[] }>(
    `/roms/${romId}/messages?since=${since}&limit=${limit}`,
  );

  return res.data;
}

async function sendMessage(
  romId: string,

  body: { deviceId: string; senderName: string; text: string; isHost: boolean },
): Promise<RomMessageDto> {
  const res = await api.authPost<{ data: RomMessageDto }>(
    `/roms/${romId}/messages`,

    body,
  );

  return res.data;
}

async function getActiveForDevice(
  deviceId: string,
): Promise<ActiveRomForDeviceResponse["data"]> {
  const res = await api.authGet<ActiveRomForDeviceResponse>(
    `/roms/active?deviceId=${encodeURIComponent(deviceId)}`,
  );

  return res.data;
}

export interface SignalingTicketResponse {
  url: string;

  ticket: string;
}

async function getSignalingTicket(
  romId: string,

  role: "guest" | "host" = "guest",
): Promise<SignalingTicketResponse> {
  const res = await api.authPost<
    { data: SignalingTicketResponse } | SignalingTicketResponse
  >(`/roms/${romId}/signaling-ticket`, { role });

  // Tolerate either {data: {...}} or flat {url, ticket}

  if (
    res &&
    typeof res === "object" &&
    "data" in res &&
    (res as { data: SignalingTicketResponse }).data
  ) {
    return (res as { data: SignalingTicketResponse }).data;
  }

  return res as SignalingTicketResponse;
}

export interface RomLookupResponse {
  rom: RomDto;
}

async function getRomByCode(code: string): Promise<RomLookupResponse> {
  // TODO(backend): confirm endpoint shape. Falls back to {data: ...} or flat.

  const res = await api.authGet<
    { data: RomLookupResponse } | RomLookupResponse
  >(`/roms/by-code/${encodeURIComponent(code)}`);

  if (
    res &&
    typeof res === "object" &&
    "data" in res &&
    (res as { data: RomLookupResponse }).data
  ) {
    return (res as { data: RomLookupResponse }).data;
  }

  return res as RomLookupResponse;
}

export const romService = {
  heartbeat,

  leave,

  leaveBeacon,

  updatePhase,

  getActiveForDevice,

  fetchMessagesSince,

  sendMessage,

  getSignalingTicket,

  getRomByCode,
};
